import type { Appointment, OpeningWindow, TimeOff } from '../api/client'
import { addDays, monthGridRange, weekdayIndex } from './dates'
import { bookedSummary, onTheBook, timeOffOn } from './dayRows'
import { clientName } from './names'
import { NO_CATEGORY_COLOUR } from '../catalog/format'

export interface MonthBooking {
  id: string
  name: string
  colour: string
  // done is completed: drawn grey, as in the week, so what's still open stands out.
  done: boolean
}

export interface MonthDay {
  date: string
  dayOfMonth: number
  // inMonth is false for the neighbouring months' days that fill out the grid.
  inMonth: boolean
  isToday: boolean
  closed: boolean
  timeOff: boolean
  count: number
  bookings: MonthBooking[]
}

export interface MonthGrid {
  days: MonthDay[]
  // The month's own days only: a neighbouring month's day is on the grid but
  // not in its total.
  count: number
  bookedPence: number
}

// monthGrid lays out the Monday-to-Sunday weeks covering date's month, each day
// with the appointments on its book in time order (no-shows and cancellations
// are for the day and week views). Time blocks stay out: they aren't appointments.
export function monthGrid(date: string, windows: OpeningWindow[], timeOff: TimeOff[], appointments: Appointment[], today: string): MonthGrid {
  const { from, to } = monthGridRange(date)
  const month = date.slice(0, 7)
  const bookings = appointments.filter((a) => onTheBook(a) && a.date >= from && a.date <= to).sort((a, b) => a.start_minute - b.start_minute)

  const days: MonthDay[] = []
  for (let day = from; day <= to; day = addDays(day, 1)) {
    const own = bookings.filter((b) => b.date === day)
    days.push({
      date: day,
      dayOfMonth: Number(day.slice(8)),
      inMonth: day.startsWith(month),
      isToday: day === today,
      closed: !windows.some((w) => w.weekday === weekdayIndex(day)),
      timeOff: timeOffOn(day, timeOff) !== null,
      count: own.length,
      bookings: own.map((b) => ({
        id: b.id,
        name: clientName(b.client),
        colour: b.service.colour ?? NO_CATEGORY_COLOUR,
        done: b.status === 'completed',
      })),
    })
  }

  return { days, ...bookedSummary(bookings.filter((b) => b.date.startsWith(month))) }
}
